import React from 'react';
import PropTypes from 'prop-types';

/**
 * GuestCrowd — six diverse pixel-art guest characters.
 *
 * Each guest is a ~14 × 34 px figure built from SVG rects.
 * The `walking` prop applies the `.gWalk` CSS animation class to
 * each guest group with a staggered delay for a natural crowd effect.
 *
 * This component must be rendered inside an <svg> element.
 *
 * @param {object}  props
 * @param {number}  props.baseX   - SVG x-coordinate for the leftmost guest.
 * @param {boolean} props.walking - Whether to play the walk animation.
 */

/**
 * Guest descriptor tuple: [skin, hair, outfit, hairStyle]
 * @typedef {[string, string, string, string]} GuestConfig
 */

/** @type {GuestConfig[]} */
const GUEST_CONFIGS = [
  // skin colour | hair colour | outfit colour | hair style
  ['#8B4513', '#1a0a00', '#e8b840', 'afro'  ],  // guest 1 — yellow dress
  ['#f5d4c8', '#5a3010', '#3a4a7a', 'short' ],  // guest 2 — navy suit
  ['#f5d4c8', '#1a0808', '#e87898', 'long'  ],  // guest 3 — pink outfit
  ['#d4a882', '#1a0808', '#7a48c8', 'bun'   ],  // guest 4 — purple sari
  ['#f5c4a8', '#c84828', '#3a7a38', 'curly' ],  // guest 5 — green jacket
  ['#e8c8b0', '#c0c0c0', '#8090a8', 'elder' ],  // guest 6 — grey elder with cane
];

/** Horizontal pixel offset between each guest. */
const GUEST_SPACING = 16;

/**
 * Renders the hair SVG rects for the given hair style.
 * @param {string} style - One of 'afro' | 'short' | 'long' | 'bun' | 'curly' | 'elder'.
 * @param {string} hair  - Hair colour hex.
 */
function Hair({ style, hair }) {
  switch (style) {
    case 'afro':
      return (
        <>
          <rect x="-1" y="-2" width="14" height="7" fill={hair} />
          <rect x="-2" y="1"  width="4"  height="6" fill={hair} />
          <rect x="11" y="1"  width="4"  height="6" fill={hair} />
        </>
      );
    case 'short':
      return (
        <>
          <rect x="1" y="-1" width="12" height="5" fill={hair} />
          <rect x="0" y="1"  width="3"  height="5" fill={hair} />
        </>
      );
    case 'long':
      return (
        <>
          <rect x="1"  y="-2" width="12" height="6" fill={hair} />
          <rect x="0"  y="2"  width="3"  height="9" fill={hair} />
          <rect x="11" y="2"  width="3"  height="9" fill={hair} />
        </>
      );
    case 'bun':
      return (
        <>
          <rect x="2" y="-3" width="10" height="6" fill={hair} />
          <rect x="4" y="-5" width="6"  height="4" fill={hair} />
          {/* Bindi detail */}
          <rect x="5" y="2"  width="2"  height="2" fill="#cc2222" />
        </>
      );
    case 'curly':
      return (
        <>
          <rect x="-1" y="-3" width="14" height="7" fill={hair} />
          <rect x="-2" y="1"  width="4"  height="6" fill={hair} />
          <rect x="12" y="1"  width="4"  height="6" fill={hair} />
        </>
      );
    case 'elder':
      return (
        <>
          <rect x="1"  y="-1" width="12" height="5" fill={hair} />
          {/* Walking cane */}
          <rect x="14" y="12" width="3"  height="18" fill="#8a6830" />
          <rect x="12" y="10" width="7"  height="3"  fill="#8a6830" />
        </>
      );
    default:
      return null;
  }
}

Hair.propTypes = {
  style: PropTypes.string.isRequired,
  hair:  PropTypes.string.isRequired,
};

function GuestCrowd({ baseX, walking }) {
  return (
    <>
      {GUEST_CONFIGS.map(([skin, hair, outfit, hairStyle], i) => {
        const animStyle = walking
          ? { animation: `gWalk 0.5s ${i * 0.07}s ease-in-out infinite` }
          : undefined;

        return (
          <g
            key={i}
            transform={`translate(${baseX + i * GUEST_SPACING}, 148)`}
            style={animStyle}
          >
            {/* Body / torso */}
            <rect x="1" y="10" width="12" height="14" fill={outfit} />
            {/* Shirt highlight */}
            <rect x="4" y="11" width="6"  height="12" fill="rgba(255,255,255,0.18)" />
            {/* Head */}
            <rect x="2" y="1"  width="10" height="10" fill={skin} />

            <Hair style={hairStyle} hair={hair} />

            {/* Eyes */}
            <rect x="3" y="4" width="3" height="3" fill="#2a1a10" />
            <rect x="8" y="4" width="3" height="3" fill="#2a1a10" />

            {/* Legs — slightly darker shade of outfit */}
            <rect x="2" y="24" width="4" height="8" fill={outfit} style={{ filter: 'brightness(0.75)' }} />
            <rect x="8" y="24" width="4" height="8" fill={outfit} style={{ filter: 'brightness(0.75)' }} />

            {/* Shoes */}
            <rect x="1" y="30" width="6" height="4" fill="#2a1a10" />
            <rect x="7" y="30" width="6" height="4" fill="#2a1a10" />
          </g>
        );
      })}
    </>
  );
}

GuestCrowd.propTypes = {
  baseX:   PropTypes.number.isRequired,
  walking: PropTypes.bool,
};

GuestCrowd.defaultProps = {
  walking: false,
};

export default GuestCrowd;
